/**
 * QR 코드 (page-partner-qr.php) — 매장 사이트 플러그인 dcraft-site-qr-salon 의 QR 생성(dqr-admin.js · dqr-admin-bar.js ·
 * dqr-tools.js)을 한 벌로 옮긴 것. 그리는 규칙을 맞춘다.
 *
 * - 부호: 오류 정정 M, 여백 6모듈(플러그인 margin: 12/2) 또는 0 — 화면 위 공통 설정([data-qr-margin-option], localStorage 에
 *   저장해 다시 바꿀 때까지 유지). 한 번 만든 부호(QRCode.create)로 세 형식을 같이 낸다.
 *   PNG 2000px(배경 투명 · 흰색) · SVG(검은 영역의 윤곽을 합친 면 path 하나 — Pathfinder Unite 결과와 같다,
 *   RGB #000000 — 인쇄소가 K100 으로 바꾼다) ·
 *   PDF(DeviceCMYK `0 0 0 1 k` 로 K 100% 단색, 50mm 한 변 — 벡터라 인쇄소가 키운다).
 * - 위치(loc)는 QR 주소의 loc 파라미터와 파일 이름에만 붙는다. 그림에는 글자를 넣지 않는다(인쇄물이 위치를 따로 표시한다).
 * - 파일 이름 {매장 아이디 앞 10자}-{yymmdd}-{위치}.png|svg|pdf, 위치가 없으면 {아이디}.png. 여러 위치는
 *   {아이디}-{yymmdd}-{개수}.zip 에 세 형식을 png/ · svg/ · pdf/ 폴더로 나눠 담는다(저장만 하는 ZIP, 압축 없음 — 플러그인 createZipBlob 형식).
 *
 * 카드마다 [data-qr-form] 하나 — data-qr-base-url(매장 QR 은 진입 주소, 페이지 주소 QR 은 매장 페이지 주소; 다른 주소는
 * 입력 칸 [data-qr-custom]) · data-qr-file-base · data-qr-bg(처음 배경). 위치 칸([data-qr-loc]) · 개수 칸([data-qr-count])은
 * 매장 QR 카드에만 있다. qrcode 패키지는 처음 만들 때 따로 불러온다(다른 화면이 싣지 않게).
 */

import { busy } from './busyButton';
import initScrollSpyNav from './scrollSpyNav';

const SIZE = 2000;
/** 여백(quiet zone) — 있음이면 사방 6모듈(플러그인 margin: 12/2, 표준 최소 4), 없음이면 0 */
const MARGIN_MODULES = 6;
/** 여백 설정 저장 키 — 이 브라우저에 남아 다시 바꿀 때까지 유지된다 */
const MARGIN_STORAGE_KEY = 'qrshop_qr_margin';
const LIGHT = { transparent: '#00000000', white: '#ffffff' };
/** PDF 한 변(pt) — 50mm. 인쇄소가 벡터로 키운다 */
const PDF_SIDE = 141.732;
/** 파일 이름의 매장 아이디는 앞 10자까지 */
const FILE_BASE_MAX = 10;

const FORMATS = [
  ['png', 'image/png'],
  ['svg', 'image/svg+xml'],
  ['pdf', 'application/pdf'],
];

let libPromise = null;

/** qrcode 패키지 — 처음 한 번만 불러온다 */
const lib = () => {
  libPromise ??= import('qrcode').then((module) => module.default || module);

  return libPromise;
};

const pad2 = (value) => String(value).padStart(2, '0');

/** yymmdd */
const today = () => {
  const now = new Date();

  return `${String(now.getFullYear()).slice(-2)}${pad2(now.getMonth() + 1)}${pad2(now.getDate())}`;
};

/** 파일 이름 조각 — 글자 · 숫자 · 한글 · - _ 만 남긴다(플러그인 safeFilePart) */
const safePart = (value, max = 40) =>
  String(value || '')
    .replace(/[^\w\-가-힣]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, max) || 'qr';

const fileName = (base, loc, ext) => {
  const id = safePart(base, FILE_BASE_MAX);

  return loc ? `${id}-${today()}-${safePart(loc)}.${ext}` : `${id}.${ext}`;
};

const zipName = (base, count) => `${safePart(base, FILE_BASE_MAX)}-${today()}-${count}.zip`;

/** 주소에 위치를 붙인다. 위치가 없으면 붙이지 않는다 */
const withLoc = (base, loc) => {
  const url = new URL(base);

  if (loc) {
    url.searchParams.set('loc', loc);
  }

  return url.toString();
};

// ── 그리기 — 부호 하나로 PNG · SVG · PDF ──────────────────────────────────

/**
 * 검은 영역의 윤곽 — 일러스트레이터 Pathfinder [합치기(Unite)] 를 한 것과 같은 결과.
 *
 * 칸마다 사각형을 나열하면 인쇄소가 겹친 도형을 다시 합쳐야 한다. 여기서는 검은 칸의 바깥 테두리(이웃이 흰 칸인 변)만
 * 모아 닫힌 고리로 잇는다. 고리는 화면 좌표(y 아래)에서 바깥 윤곽이 시계 방향, 구멍이 반시계 방향이라 nonzero 채우기로
 * 그대로 구멍이 뚫린다. 꼭짓점에서 나가는 변이 둘이면(대각선으로만 닿은 칸) 오른쪽으로 꺾는 변을 골라 각 덩어리를
 * 따로 닫는다. 좌표는 모듈 단위이고 여백(margin)만큼 밀어 둔다.
 *
 * @returns {number[][][]} 고리 목록 — 고리는 [x, y] 꼭짓점 목록(직선 위의 중간점은 뺀다)
 */
const darkOutline = (modules, margin) => {
  const size = modules.size;
  const dark = (row, col) =>
    row >= 0 && col >= 0 && row < size && col < size && !!modules.get(row, col);
  const key = (x, y) => `${x},${y}`;
  /** 시작 꼭짓점 → 끝 꼭짓점들(대각선 접점은 둘) */
  const edges = new Map();
  const add = (x1, y1, x2, y2) => {
    const from = key(x1, y1);

    if (!edges.has(from)) {
      edges.set(from, []);
    }

    edges.get(from).push([x2, y2]);
  };

  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size; col += 1) {
      if (!dark(row, col)) {
        continue;
      }

      const x = col + margin;
      const y = row + margin;

      // 시계 방향(y 아래): 위 → 오른쪽 → 아래 → 왼쪽. 검은 영역이 진행 방향의 오른편에 온다
      if (!dark(row - 1, col)) add(x, y, x + 1, y);
      if (!dark(row, col + 1)) add(x + 1, y, x + 1, y + 1);
      if (!dark(row + 1, col)) add(x + 1, y + 1, x, y + 1);
      if (!dark(row, col - 1)) add(x, y + 1, x, y);
    }
  }

  const loops = [];

  for (const [start, targets] of edges) {
    while (targets.length) {
      const [sx, sy] = start.split(',').map(Number);
      const points = [[sx, sy]];
      let [x, y] = targets.shift();
      let dx = x - sx;
      let dy = y - sy;

      while (x !== sx || y !== sy) {
        points.push([x, y]);

        const next = edges.get(key(x, y)) || [];
        // 오른쪽으로 꺾기 → 직진 → 왼쪽으로 꺾기 순으로 고른다
        const order = [
          [-dy, dx],
          [dx, dy],
          [dy, -dx],
        ];
        let picked = -1;

        for (const [px, py] of order) {
          picked = next.findIndex(([nx, ny]) => nx - x === px && ny - y === py);

          if (picked >= 0) {
            break;
          }
        }

        if (picked < 0) {
          break;
        }

        const [nx, ny] = next.splice(picked, 1)[0];
        dx = nx - x;
        dy = ny - y;
        x = nx;
        y = ny;
      }

      // 직선 위의 중간점 제거
      const simplified = points.filter(([px, py], index) => {
        const [ax, ay] = points[(index + points.length - 1) % points.length];
        const [bx, by] = points[(index + 1) % points.length];

        return !((ax === px && px === bx) || (ay === py && py === by));
      });

      loops.push(simplified);
    }
  }

  return loops;
};

const renderPng = async (QRCode, url, { light, margin }) => {
  const canvas = document.createElement('canvas');

  await QRCode.toCanvas(canvas, url, {
    width: SIZE,
    margin,
    errorCorrectionLevel: 'M',
    color: { dark: '#000000', light },
  });

  // 라이브러리가 캔버스에 인라인 width/height(px)를 박는다. CSS 로 줄여 보이게 지운다
  canvas.style.removeProperty('width');
  canvas.style.removeProperty('height');

  return canvas;
};

const toPngBlob = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('PNG 로 바꾸지 못했습니다.'))),
      'image/png'
    );
  });

/** SVG — 좌표는 모듈 단위, 검은 영역은 합쳐진 윤곽(darkOutline) 면 path 하나. 흰 배경이면 rect 를 깐다 */
const renderSvg = (modules, { white, margin }) => {
  const total = modules.size + margin * 2;
  const path = darkOutline(modules, margin)
    .map((loop) => `M${loop.map(([x, y]) => `${x} ${y}`).join('L')}Z`)
    .join('');
  const parts = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 ${total} ${total}" shape-rendering="crispEdges">`,
  ];

  if (white) {
    parts.push(`<rect width="${total}" height="${total}" fill="#ffffff"/>`);
  }

  parts.push(`<path fill="#000000" d="${path}"/>`, '</svg>');

  return parts.join('');
};

/**
 * PDF — 한 쪽, DeviceCMYK. 검은 모듈은 `0 0 0 1 k`(K 100%), 흰 배경은 `0 0 0 0 k`(종이색), 투명이면 배경을 그리지 않는다.
 * 내용이 전부 ASCII 라 글자 수가 곧 바이트 수다(xref 오프셋).
 */
const renderPdf = (modules, { white, margin }) => {
  const total = modules.size + margin * 2;
  const scale = (PDF_SIDE / total).toFixed(6);
  const content = [`q ${scale} 0 0 ${scale} 0 0 cm`];

  if (white) {
    content.push(`0 0 0 0 k 0 0 ${total} ${total} re f`);
  }

  // PDF 는 원점이 왼쪽 아래 — y 를 뒤집는다(꼭짓점 좌표라 total - y)
  content.push('0 0 0 1 k');
  darkOutline(modules, margin).forEach((loop) => {
    loop.forEach(([x, y], index) => content.push(`${x} ${total - y} ${index ? 'l' : 'm'}`));
    content.push('h');
  });
  content.push('f Q');

  const stream = content.join('\n');
  const side = PDF_SIDE.toFixed(3);
  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${side} ${side}] /Contents 4 0 R >>`,
    `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`,
  ];

  let pdf = '%PDF-1.4\n';
  const offsets = [];

  objects.forEach((body, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${body}\nendobj\n`;
  });

  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.forEach((offset) => {
    pdf += `${String(offset).padStart(10, '0')} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`;

  return pdf;
};

/** 주소 하나 → 세 형식. canvas 는 미리보기용 */
const renderAll = async (url, { bg, margin }) => {
  const QRCode = await lib();
  const modules = QRCode.create(url, { errorCorrectionLevel: 'M' }).modules;
  const white = bg === 'white';
  const canvas = await renderPng(QRCode, url, { light: LIGHT[bg], margin });

  return {
    canvas,
    blobs: {
      png: await toPngBlob(canvas),
      svg: new Blob([renderSvg(modules, { white, margin })], { type: 'image/svg+xml' }),
      pdf: new Blob([renderPdf(modules, { white, margin })], { type: 'application/pdf' }),
    },
  };
};

// ── ZIP(저장만, 압축 없음) ────────────────────────────────────────────────

let crcTable = null;

const crc32 = (bytes) => {
  if (!crcTable) {
    crcTable = new Uint32Array(256);

    for (let n = 0; n < 256; n += 1) {
      let c = n;

      for (let k = 0; k < 8; k += 1) {
        c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      }

      crcTable[n] = c >>> 0;
    }
  }

  let crc = 0xffffffff;

  for (let i = 0; i < bytes.length; i += 1) {
    crc = crcTable[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }

  return (crc ^ 0xffffffff) >>> 0;
};

const push16 = (out, value) => out.push(value & 0xff, (value >>> 8) & 0xff);

const push32 = (out, value) =>
  out.push(value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff);

/** @param {{name: string, data: Uint8Array}[]} files */
const zipBlob = (files) => {
  const encoder = new TextEncoder();
  const now = new Date();
  const dosTime =
    (now.getHours() << 11) | (now.getMinutes() << 5) | Math.floor(now.getSeconds() / 2);
  const dosDate =
    ((Math.max(1980, now.getFullYear()) - 1980) << 9) | ((now.getMonth() + 1) << 5) | now.getDate();
  const localParts = [];
  const centralParts = [];
  let offset = 0;

  files.forEach((file) => {
    const nameBytes = encoder.encode(file.name);
    const crc = crc32(file.data);
    const size = file.data.length;

    const local = [];
    push32(local, 0x04034b50);
    push16(local, 20);
    push16(local, 0x0800); // UTF-8 이름
    push16(local, 0);
    push16(local, dosTime);
    push16(local, dosDate);
    push32(local, crc);
    push32(local, size);
    push32(local, size);
    push16(local, nameBytes.length);
    push16(local, 0);

    const central = [];
    push32(central, 0x02014b50);
    push16(central, 20);
    push16(central, 20);
    push16(central, 0x0800);
    push16(central, 0);
    push16(central, dosTime);
    push16(central, dosDate);
    push32(central, crc);
    push32(central, size);
    push32(central, size);
    push16(central, nameBytes.length);
    push16(central, 0);
    push16(central, 0);
    push16(central, 0);
    push16(central, 0);
    push32(central, 0);
    push32(central, offset);

    localParts.push(new Uint8Array(local), nameBytes, file.data);
    centralParts.push(new Uint8Array(central), nameBytes);
    offset += local.length + nameBytes.length + size;
  });

  const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0);
  const end = [];
  push32(end, 0x06054b50);
  push16(end, 0);
  push16(end, 0);
  push16(end, files.length);
  push16(end, files.length);
  push32(end, centralSize);
  push32(end, offset);
  push16(end, 0);

  return new Blob([...localParts, ...centralParts, new Uint8Array(end)], {
    type: 'application/zip',
  });
};

// ── 공통 설정 — 여백 ──────────────────────────────────────────────────────

/** 지금 여백(모듈). 저장된 값이 없거나 읽지 못하면 있음 */
let marginModules = MARGIN_MODULES;

const readMargin = () => {
  try {
    return window.localStorage.getItem(MARGIN_STORAGE_KEY) === 'off' ? 0 : MARGIN_MODULES;
  } catch (error) {
    return MARGIN_MODULES;
  }
};

const saveMargin = (modules) => {
  try {
    window.localStorage.setItem(MARGIN_STORAGE_KEY, modules ? 'on' : 'off');
  } catch (error) {
    // 저장 못 해도 이번 화면에서는 적용된다
  }
};

/** 여백 있음 · 없음 segmented([data-qr-margin-option="on|off"]) — 세 카드 모두에 적용, 브라우저에 저장 */
const setupMargin = (root) => {
  const options = Array.from(root.querySelectorAll('[data-qr-margin-option]'));

  if (!options.length) {
    return;
  }

  marginModules = readMargin();

  const reflect = () => {
    options.forEach((option) =>
      option.setAttribute(
        'aria-pressed',
        String((option.dataset.qrMarginOption === 'on') === marginModules > 0)
      )
    );
  };

  options.forEach((option) => {
    option.addEventListener('click', () => {
      marginModules = option.dataset.qrMarginOption === 'on' ? MARGIN_MODULES : 0;
      saveMargin(marginModules);
      reflect();
    });
  });

  reflect();
};

// ── 카드 하나 ─────────────────────────────────────────────────────────────

const setupForm = (form) => {
  const custom = form.querySelector('[data-qr-custom]');
  const locInput = form.querySelector('[data-qr-loc]');
  const countInput = form.querySelector('[data-qr-count]');
  const urlEl = form.querySelector('[data-qr-url]');
  const button = form.querySelector('[data-qr-generate]');
  const status = form.querySelector('[data-qr-status]');
  const preview = form.querySelector('[data-qr-preview]');
  const holder = form.querySelector('[data-qr-holder]');
  const downloads = Array.from(form.querySelectorAll('[data-qr-download]'));
  const copy = form.querySelector('[data-qr-copy]');
  const bgButtons = Array.from(form.querySelectorAll('[data-qr-bg-option]'));
  const fileBase = form.dataset.qrFileBase || 'qr';

  let bg = form.dataset.qrBg in LIGHT ? form.dataset.qrBg : 'white';
  let urls = [];

  const showStatus = (text, state) => {
    if (!status) {
      return;
    }

    status.hidden = !text;
    status.classList.toggle('notice--error', state === 'error');
    status.classList.toggle('notice--success', state === 'success');
    status.querySelector('.notice__text').textContent = text;
  };

  const baseUrl = () => (custom ? custom.value.trim() : form.dataset.qrBaseUrl || '');

  /** 위치 목록 — 적은 위치 하나, 아니면 개수만큼 01 부터, 둘 다 없으면 위치 없이 하나 */
  const locValues = () => {
    const loc = locInput?.value.trim() || '';

    if (loc) {
      return [loc];
    }

    const count = Number.parseInt(countInput?.value || '0', 10);

    return count >= 1 ? Array.from({ length: count }, (_, index) => pad2(index + 1)) : [''];
  };

  /** 만들 주소들. 주소가 틀리면 빈 배열 */
  const buildUrls = () => {
    const base = baseUrl();

    if (!base) {
      return [];
    }

    try {
      return locValues().map((loc) => ({ loc, url: withLoc(base, loc) }));
    } catch (error) {
      return [];
    }
  };

  const refreshUrl = () => {
    if (!urlEl) {
      return;
    }

    const list = buildUrls();

    if (!list.length) {
      urlEl.textContent = '';

      return;
    }

    urlEl.textContent = list.length > 1 ? `${list[0].url} 외 ${list.length - 1}개` : list[0].url;
  };

  /** 저장 버튼 하나에 파일을 건다. blob 이 없으면 숨긴다 */
  const setDownload = (link, blob, name) => {
    if (link.dataset.objectUrl) {
      URL.revokeObjectURL(link.dataset.objectUrl);
      delete link.dataset.objectUrl;
    }

    link.hidden = !blob;

    if (!blob) {
      return;
    }

    const objectUrl = URL.createObjectURL(blob);
    link.dataset.objectUrl = objectUrl;
    link.href = objectUrl;
    link.download = name;
  };

  const linkFor = (kind) => downloads.find((link) => link.dataset.qrDownload === kind);

  const generate = async () => {
    const list = buildUrls();

    if (!list.length) {
      showStatus(
        custom ? 'https:// 로 시작하는 주소를 적어 주세요.' : '만들 주소가 없습니다.',
        'error'
      );

      return;
    }

    const base = custom ? new URL(list[0].url).hostname.replace(/^www\./, '') : fileBase;

    const release = busy(button, '만드는 중…');
    showStatus('', '');

    try {
      const results = [];

      for (const item of list) {
        // eslint-disable-next-line no-await-in-loop
        results.push({ ...item, ...(await renderAll(item.url, { bg, margin: marginModules })) });
      }

      if (holder) {
        holder.replaceChildren(results[0].canvas);
      }

      if (results.length > 1) {
        const entries = [];

        for (const result of results) {
          for (const [ext] of FORMATS) {
            entries.push({
              name: `${ext}/${fileName(base, result.loc, ext)}`,
              // eslint-disable-next-line no-await-in-loop
              data: new Uint8Array(await result.blobs[ext].arrayBuffer()),
            });
          }
        }

        FORMATS.forEach(([ext]) => setDownload(linkFor(ext), null, ''));
        setDownload(linkFor('zip'), zipBlob(entries), zipName(base, results.length));
        showStatus(
          `QR ${results.length}개를 만들었습니다. PNG · SVG · PDF 를 ZIP 한 파일로 저장하세요.`,
          'success'
        );
      } else {
        FORMATS.forEach(([ext]) =>
          setDownload(linkFor(ext), results[0].blobs[ext], fileName(base, results[0].loc, ext))
        );
        setDownload(linkFor('zip'), null, '');
      }

      urls = results.map((item) => item.url);

      if (preview) {
        preview.hidden = false;
      }
    } catch (error) {
      showStatus(`QR 을 만들지 못했습니다. ${error.message || ''}`.trim(), 'error');
    } finally {
      release();
    }
  };

  bgButtons.forEach((option) => {
    option.addEventListener('click', () => {
      bg = option.dataset.qrBgOption in LIGHT ? option.dataset.qrBgOption : bg;
      bgButtons.forEach((other) => other.setAttribute('aria-pressed', String(other === option)));
    });
  });

  [locInput, countInput, custom].forEach((input) => input?.addEventListener('input', refreshUrl));

  button?.addEventListener('click', generate);

  copy?.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(urls.join('\n'));
      showStatus(
        urls.length > 1 ? `주소 ${urls.length}개를 복사했습니다.` : '주소를 복사했습니다.',
        'success'
      );
    } catch (error) {
      showStatus('복사하지 못했습니다. 주소를 직접 골라 복사해 주세요.', 'error');
    }
  });

  refreshUrl();
};

const InitPartnerQr = () => {
  const root = document.querySelector('[data-partner-qr]');

  if (!root) {
    return;
  }

  const navEl = document.querySelector('[data-qr-nav]');

  if (navEl) {
    initScrollSpyNav(
      navEl,
      Array.from(root.querySelectorAll('[data-nav-label]')).map((section) => ({
        el: section,
        label: section.dataset.navLabel,
      }))
    );
  }

  setupMargin(root);
  root.querySelectorAll('[data-qr-form]').forEach(setupForm);
};

export default InitPartnerQr;
